@let hasShiftUpdatePermission =
  can(RoleAction.Update, RolePermissionSubject.Shift);
@let hasShiftReadPermission = can(RoleAction.Read, RolePermissionSubject.Shift);
@let forbiddenMessage = 'general.forbidden_action' | translate;
@let hasServiceUpdatePermission =
  can(RoleAction.Update, RolePermissionSubject.Service);
@let hasShiftEditPermission =
  hasServiceActivitySignal()
    ? hasShiftUpdatePermission && hasServiceUpdatePermission
    : hasShiftUpdatePermission;
@let isFormPristine = !!(shiftDrawerCloseHelperService.isFormPristine$ | async);
@let isAuditTrailEnabled =
  !!('audit-trail-global' | featureFlagPure | async) &&
  !!('audit-trail-shift' | featureFlagPure | async);

<nz-drawer
  wilsonDrawerSettingsManager
  [drawerId]="drawerId"
  [nzSize]="'large'"
  [nzVisible]="true"
  [nzMask]="false"
  [nzFooter]="drawerFooter"
  [nzExtra]="extraTemplate"
  [nzClosable]="true"
  [nzCloseIcon]="'close'"
  [nzMaskClosable]="false"
  (nzOnClose)="closeDrawer.emit()"
>
  <ng-container *nzDrawerContent>
    <wilson-nz-drawer-content [drawerId]="drawerId" [nzSize]="'large'">
      @if (shift(); as selectedShift) {
        <div class="shift-status">
          <wilson-shift-status [shift]="selectedShift"></wilson-shift-status>
        </div>

        <section class="drawer-section">
          <wilson-information-section
            [shift]="selectedShift"
            (refreshActivities)="
              reloadShiftAndUpdateTimeline({ updatePublicationStatus: true })
            "
            (updatedNecessaryStay)="updateShiftNecessaryStay($event)"
          ></wilson-information-section>
        </section>

        <section class="drawer-section">
          <wilson-shift-reported-section
            [shift]="selectedShift"
            (refresh)="
              reloadShiftAndUpdateTimeline({ updatePublicationStatus: true })
            "
          ></wilson-shift-reported-section>
        </section>

        <section class="drawer-section drawer-section--separated">
          <wilson-collapsible-shift-quick-check
            [userId]="selectedShift.userId ?? selectedShift.user?.id"
            [shiftValidationDetails]="
              (shiftQuickCheckService.shiftValidation$ | async) || []
            "
            [isLoading]="
              !!(shiftQuickCheckService.isLoadingValidation$ | async)
            "
            [isExpandedOnOpen]="true"
            [headerTranslationKey]="'page.simplified_shift_drawer.validation'"
          ></wilson-collapsible-shift-quick-check>
        </section>
      } @else {
        <nz-skeleton
          [nzActive]="true"
          [nzParagraph]="{ rows: 6 }"
        ></nz-skeleton>
      }
    </wilson-nz-drawer-content>
  </ng-container>
  <ng-template #drawerFooter>
    <div class="drawer__footer">
      <wilson-last-update-status
        [updatedAt]="updatedAtSignal()"
        [isUpdated]="isSuccessfullySavedSignal()"
        [isClickable]="isAuditTrailEnabled"
        (clickEvent)="toggleChangeHistoryDrawer()"
      ></wilson-last-update-status>
    </div>
  </ng-template>

  <ng-template #extraTemplate>
    <wilson-nz-drawer-header [drawerId]="drawerId">
      @if (shiftViewers().length) {
        <wilson-viewers-avatars
          drawerHeaderStart
          class="me-2"
          [viewers]="shiftViewers()"
          [tooltipTranslationKey]="
            'page.shift_timeline.presence.shift_viewers_label'
          "
        ></wilson-viewers-avatars>
      }

      <div class="drawer-extra">
        @if (shift(); as selectedShift) {
          <wilson-shift-form-view-button
            class="me-2"
            nz-tooltip
            [nzTooltipTitle]="
              !hasShiftReadPermission
                ? forbiddenMessage
                : ('page.shift_timeline.drawer.shift_form.view_shift'
                  | translate)
            "
            nzTooltipPlacement="bottom"
            [shiftId]="selectedShift.id"
            [disabled]="
              !hasShiftReadPermission || isShiftUpdateOperationOngoingSignal()
            "
            [isFormPristine]="isFormPristine"
            data-test-id="wilson-timeline-drawer:view-shift"
          ></wilson-shift-form-view-button>

          <wilson-more-shift-actions-dropdown
            class="me-2"
            [shift]="selectedShift"
            [renderMoveOption]="true"
            [isMoveAllowed]="[selectedShift] | canAllShiftBeModified"
            [disableSaveAsTemplate]="isFormInvalid()"
            [duplicateShiftInNewTab]="true"
            [disabled]="isShiftUpdateOperationOngoingSignal()"
            [showIconOnly]="true"
            (convertShiftToStandbySuccessful)="reloadShiftAndUpdateTimeline()"
            (deleteTriggered)="deletionConfirmed.emit(selectedShift)"
            (filledShiftWithPlannedTimeSuccessful)="
              reloadShiftAndUpdateTimeline()
            "
            (moveShiftSuccess)="reloadShiftAndUpdateTimeline()"
          ></wilson-more-shift-actions-dropdown>

          @let areAllActivitiesFinished =
            selectedShift.activities | areAllActivitiesFinished;
          @if (!areAllActivitiesFinished) {
            <wilson-shift-form-edit-button
              class="me-2"
              nz-tooltip
              [nzTooltipTitle]="
                !hasShiftEditPermission
                  ? forbiddenMessage
                  : ('page.shift_timeline.drawer.shift_form.edit_shift'
                    | translate)
              "
              nzTooltipPlacement="bottom"
              [shiftId]="selectedShift.id"
              [isFormPristine]="isFormPristine"
              [activities]="selectedShift.activities"
              [disabled]="
                !hasShiftEditPermission ||
                isShiftUpdateOperationOngoingSignal() ||
                areAllActivitiesFinished
              "
              data-test-id="wilson-timeline-drawer:edit-shift"
            ></wilson-shift-form-edit-button>
          }
          @if (
            selectedShift?.publicationStatus !== PublicationStatus.Published
          ) {
            <wilson-shift-form-publish-button
              nz-tooltip
              [nzTooltipTitle]="
                !hasShiftUpdatePermission ? forbiddenMessage : null
              "
              [selectedShift]="selectedShift"
              (reloadDataEvent)="reloadShiftAndUpdateTimeline()"
              [disabled]="
                !hasShiftUpdatePermission ||
                isShiftUpdateOperationOngoingSignal()
              "
              data-test-id="wilson-timeline-drawer:publish-shift"
            ></wilson-shift-form-publish-button>
          }

          <wilson-revert-shift-button
            [shift]="selectedShift"
            [disabled]="isShiftUpdateOperationOngoingSignal()"
            [showIconOnly]="true"
            (updateSuccess)="reloadShiftAndUpdateTimeline()"
            data-test-id="wilson-timeline-drawer:revert-shift"
          ></wilson-revert-shift-button>

          <wilson-send-shift-back-to-assignee-button
            class="me-2"
            nz-tooltip
            [nzTooltipTitle]="
              !hasShiftUpdatePermission ? forbiddenMessage : null
            "
            [shift]="selectedShift"
            [disabled]="
              !hasShiftUpdatePermission || isShiftUpdateOperationOngoingSignal()
            "
            [showIconOnly]="true"
            (updateSuccess)="reloadShiftAndUpdateTimeline()"
            data-test-id="wilson-timeline-drawer:send-back-to-assignee"
          ></wilson-send-shift-back-to-assignee-button>

          <wilson-accept-shift-button
            nz-tooltip
            [nzTooltipTitle]="
              !hasShiftUpdatePermission ? forbiddenMessage : null
            "
            [shift]="selectedShift"
            [disabled]="
              !hasShiftUpdatePermission || isShiftUpdateOperationOngoingSignal()
            "
            (updateSuccess)="reloadShiftAndUpdateTimeline()"
            [textOnly]="true"
            data-test-id="wilson-timeline-drawer:accept-shift"
          ></wilson-accept-shift-button>
        }
      </div>
    </wilson-nz-drawer-header>
  </ng-template>

  @if (isAuditTrailEnabled) {
    <wilson-audit-trail-drawer></wilson-audit-trail-drawer>
  }
</nz-drawer>
